import { FaCopy, FaExternalLinkAlt, FaCheckCircle } from 'react-icons/fa';
import { CodeDetailResponse } from '../../types/code.types';
import toast from 'react-hot-toast';

interface CodeDetailProps {
  code: CodeDetailResponse;
}

const CodeDetail = ({ code }: CodeDetailProps) => {
  const copyCode = () => {
    navigator.clipboard.writeText(code.code);
    toast.success('Code copied!');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center space-x-3 mb-2">
              <h1 className="text-4xl font-bold text-primary-600">{code.code}</h1>
              <span className="px-4 py-1 bg-primary-100 text-primary-700 rounded-full">
                {code.codeType}
              </span>
              {code.billable && (
                <span className="px-3 py-1 bg-success/10 text-success rounded-full flex items-center space-x-1">
                  {/* @ts-ignore */}
                  <FaCheckCircle />
                  <span>Billable</span>
                </span>
              )}
            </div>
            <p className="text-xl text-gray-700 mt-2">{code.description}</p>
          </div>
          <button
            onClick={copyCode}
            className="p-3 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
          >
            {/* @ts-ignore */}
            <FaCopy className="text-xl" />
          </button>
        </div>
      </div>

      {/* Details */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-4">Official Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {code.category && (
            <div>
              <p className="text-sm text-gray-500">Category</p>
              <p className="text-gray-800 font-medium">{code.category}</p>
            </div>
          )}
          {code.chapter && (
            <div>
              <p className="text-sm text-gray-500">Chapter</p>
              <p className="text-gray-800 font-medium">{code.chapter}</p>
            </div>
          )}
          <div>
            <p className="text-sm text-gray-500">Source</p>
            <p className="text-gray-800 font-medium">{code.officialSource}</p>
          </div>
         {code.effectiveDate && (
            <div>
              <p className="text-sm text-gray-500">Effective Date</p>
              <p className="text-gray-800 font-medium">
                {new Date(code.effectiveDate).toLocaleDateString()}
              </p>
            </div>
          )} 
        </div>

        {Object.keys(code.additionalInfo).length > 0 && (
          <div className="mt-6">
            <h3 className="text-lg font-bold mb-3">Additional Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(code.additionalInfo).map(([key, value]) => (
                <div key={key}>
                  <p className="text-sm text-gray-500">{key}</p>
                  <p className="text-gray-800">{value}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Verification Links */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-4">🔗 Verify This Code</h2>
        <p className="text-gray-600 mb-4">
          Always verify codes at official sources before billing:
        </p>
        <div className="space-y-2">
          <a
            href={`https://www.cms.gov`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 text-primary-600 hover:text-primary-700"
          >
            {/* @ts-ignore */}
            <FaExternalLinkAlt />
            <span>CMS Official Website</span>
          </a>
          <a
            href={`https://icd.codes/icd10cm/${code.code}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 text-primary-600 hover:text-primary-700"
          >
            {/* @ts-ignore */}
            <FaExternalLinkAlt />
            <span>ICD.Codes Verification</span>
          </a>
        </div>
      </div>

      {/* Related Codes */}
      {code.relatedCodes && code.relatedCodes.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold mb-4">💡 Related Codes</h2>
          <div className="space-y-3">
            {code.relatedCodes.map((related, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <span className="font-bold text-primary-600">{related.code}</span>
                  <span className="text-sm text-gray-500">({related.relationship})</span>
                </div>
                <p className="text-gray-700 mt-1">{related.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
        <p className="text-yellow-800">
          <strong>⚠️ Disclaimer:</strong> This is a reference tool only. Always verify codes
          before billing. We are not liable for coding errors. Consult with certified medical
          coders when needed.
        </p>
      </div>
    </div>
  );
};

export default CodeDetail;